import React, { forwardRef, useContext } from 'react'
import PropTypes from 'prop-types'
import { css, cx } from '@linaria/core'
import { ContentRemove } from '@fs/zion-icon'
import { boolAttr } from '@fs/zion-frontend-friends'

import { colors, useTheme } from '../theme'
import { IconButton } from '../buttons/icon-button'
import BaseButton from '../buttons/BaseButton'
import Ellipsis from '../ellipsis'

import { colorCss, highChipCss } from './colorStyles'
import { whiteTheme, blueTheme, grayTheme } from '../theme/themeSubsets'
import BaseChip from './BaseChip'
import { chipCss } from './Chip'
import { WhiteThemeContext } from '../quarks-authorized-use-only'

type InputChipProps = PropTypes.InferProps<typeof propTypes>

const containerCss = css`
  display: flex;
  position: relative;
  align-items: center;
`

const buttonCss = css`
  border: 0;
  height: 32px;
  padding: 2px 11px;
  line-height: inherit;
  &[data-dismissable] {
    padding-inline-end: 29px;
  }
`

const iconButtonCss = css`
  --button-outline-offset: -2px;
  position: absolute;
  top: 3.645px; // Centers X icon vertically due to absolute positioning
  inset-inline-end: 8px;
`

const Chip = forwardRef((props, ref): JSX.Element => <BaseChip as={BaseButton} ref={ref} {...props} />)

function InputChip({
  buttonProps,
  description,
  disabled = false,
  dismissAriaLabel,
  dismissLinkName,
  emphasized = false,
  label,
  linkName,
  onDismiss,
  selected = false,
  avatarProps,
  Icon,
  ...props
}: InputChipProps): JSX.Element {
  const colorTheme = selected ? blueTheme : grayTheme
  const isWhite = useContext(WhiteThemeContext)
  const isChecked = selected || emphasized
  const needsGrayColor = !selected && emphasized && isWhite

  let dismissIconColor = colors.transparent.gray90
  if (disabled) dismissIconColor = colors.transparent.gray10
  if (needsGrayColor) dismissIconColor = 'rgba(0, 0, 0, 0.8)' // Matches data-gray-chip in highChipCss

  const isNight = useTheme() === 'night'

  return (
    <div
      className={cx(highChipCss, colorTheme, containerCss)}
      data-white={boolAttr(isWhite)}
      data-selected={boolAttr(isChecked)}
      data-night={boolAttr(isNight)}
      aria-disabled={disabled}
      data-gray-chip={boolAttr(needsGrayColor)}
    >
      <Ellipsis
        as={Chip}
        {...props}
        label={label}
        description={description}
        className={cx(buttonCss, chipCss, colorCss)}
        disabled={disabled}
        linkName={linkName}
        aria-checked={props.role === 'checkbox' ? selected : undefined}
        data-dismissable={boolAttr(onDismiss)}
        Icon={Icon}
        avatarProps={avatarProps}
      >
        {label}
      </Ellipsis>

      {/* Dismiss button for the InputChip. For accessibility purposes, this needs to remain a sibling to BaseButton to avoid nested affordances. */}
      {onDismiss && (
        <div className={cx(iconButtonCss, isChecked && !disabled && whiteTheme)}>
          <IconButton
            {...buttonProps}
            disabled={disabled}
            Icon={ContentRemove}
            iconColor={dismissIconColor}
            linkName={dismissLinkName || `Dismiss ${linkName}`}
            onClick={onDismiss}
            size="xs"
            title={dismissAriaLabel}
          />
        </div>
      )}
    </div>
  )
}

export const propTypes = {
  /** Tooltip description.  If present, a DescriptionTooltip is placed on the chip with this description. */
  description: PropTypes.string,

  /** The text displayed in the chip */
  label: PropTypes.string,

  /** Optional. Name for analytics tracking. */
  linkName: PropTypes.string,

  /** Callback for when the user clicks on the chip */
  onDismiss: PropTypes.func,

  /** Whether or not this chip is currently selected. */
  selected: PropTypes.bool,

  /** Show if the chip is clickable or not */
  disabled: PropTypes.bool,

  /** Turn the chip blue (because the user has chosen it) */
  emphasized: PropTypes.bool,

  /** Callback for when the user clicks on the chip */
  onClick: PropTypes.func,

  /** Aria Label to read for the dismiss button */
  dismissAriaLabel: PropTypes.string,

  /** Optional. Name to track the dismiss button. If omitted, default text will be created using the chip's or chips' linkName. */
  dismissLinkName: PropTypes.string,

  /** Debounce time in ms. */
  debounce: PropTypes.number,

  /** Label to help with accessibility. */
  'aria-label': PropTypes.string,

  /** The value associated with this chip. */
  value: PropTypes.string,

  /** Role for this chip. ex) if selected the chip role will be checkbox, otherwise, undefined. */
  role: PropTypes.string,

  /** Props to pass on to the IconButton */
  buttonProps: PropTypes.object,

  /** A config object for the Avatar (You may not change the size) */
  avatarProps: PropTypes.shape({
    /** Copied from avatar */
    src: PropTypes.string,
    monogram: PropTypes.string,
    presence: PropTypes.oneOf(['online', 'offline'] as const),
    sex: PropTypes.oneOf(['female', 'male', 'unknown'] as const),
    gray: PropTypes.bool,
    imageFailedCallback: PropTypes.func,
  }),

  /** Optional icon displayed to the left of the label. */
  Icon: PropTypes.func,
}

InputChip.propTypes = propTypes
export default InputChip
